import { Checkbox } from "@blueprintjs/core";
import { ButtonContainer, IDataTableColumn } from "nsitools-react";
import * as React from "react";
import { useHistory } from "react-router";
import { Link } from "react-router-dom";

import { ERequestState, ERequestType, RequestApi, RequestGridDto, RequestSearch } from "../../../../api";
import { EditButton, SearchTablePage, ViewButton } from "../../../../components";
import { useGlobalData } from "../../../../contexts";
import { ERoutes } from "../../../../ERoutes";
import { useApiService, useTl } from "../../../../hooks";
import { ETLCodes } from "../../../../locales";
import { getRouteStr } from "../../../../utils/RequestRouteUtil";
import { getSearchRouteStr } from "../../../../utils/RequestSearchRouteUtil";

export interface IRequestListPageProps {}

export const RequestListPage: React.FunctionComponent<IRequestListPageProps> = (props) => {
  const api = useApiService(RequestApi);
  const { t } = useTl();
  const history = useHistory();
  const { currentLanguage } = useGlobalData();

  const redirectToRequest = React.useCallback(
    async (requestId: number, requestType: ERequestType) => {
      const subRequestId = await api.getSubRequestTypeId(requestId, requestType);
      const link = "#" + (await getRouteStr(requestType, subRequestId, "view"));
      console.log("link ", link);
      const anchor: HTMLAnchorElement = document.createElement("a");
      anchor.href = link;
      anchor.target = "_blank";
      anchor.click();
      setTimeout(() => URL.revokeObjectURL(link));
    },
    [api]
  );

  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        computed: true,
        disableSorting: true,
        fieldName: "actions",
        autoFitContent: true,
        render: (row: RequestGridDto) => (
          <ButtonContainer>
            <ViewButton
              minimal={true}
              onClick={async () => {
                const subRequestId = await api.getSubRequestTypeId(row.requestId, row.requestTypeCode);
                const p = await getRouteStr(row.requestTypeCode, subRequestId, "view");
                history.push(p);
              }}
            />

            <EditButton
              disabled={row.requestStateCode === ERequestState.EXECUTED}
              minimal={true}
              onClick={async () => {
                const subRequestId = await api.getSubRequestTypeId(row.requestId, row.requestTypeCode);
                const p = await getRouteStr(row.requestTypeCode, subRequestId, "edit");
                history.push(p);
              }}
            />
          </ButtonContainer>
        ),
      },
      {
        header: () => t(ETLCodes.RequestBlocked),
        fieldName: "isBlocked",
        alignment: "center",
        render: (row: RequestGridDto) => <Checkbox checked={row.isBlocked} disabled />,
      },
      {
        header: () => t(ETLCodes.RequestType),
        fieldName: "requestType",
        render: (row: RequestGridDto) => (
          <Link target="_blank" to={getSearchRouteStr(row.requestTypeCode)}>
            {row.requestType}
          </Link>
        ),
      },
      {
        header: () => t(ETLCodes.RequestNumber),
        fieldName: "requestNumber",
      },
      {
        header: () => t(ETLCodes.ParentRequest),
        fieldName: "parentRequest",
        render: (row: RequestGridDto) => {
          if (row.parentRequestId) {
            return (
              // eslint-disable-next-line jsx-a11y/anchor-is-valid
              <a onClick={() => redirectToRequest(row.parentRequestId, row.parentRequestTypeCode)}>
                {row.parentRequest}
              </a>
            );
          } else {
            return "/";
          }
        },
      },
      {
        header: () => t(ETLCodes.RequestDate),
        fieldName: "requestDate",
      },
      {
        header: () => t(ETLCodes.RequestState),
        fieldName: "requestState",
      },
      {
        header: () => t(ETLCodes.Contact),
        fieldName: "contact",
        render: (row: RequestGridDto) => (
          <Link target="_blank" to={`${ERoutes.people}/${row.personId}/detail/view`}>
            {row.contact}
          </Link>
        ),
      },
      {
        header: () => t(ETLCodes.Invoice),
        fieldName: "invoiceNumber",
      },
    ],
    [api, history, redirectToRequest, t]
  );

  const search = React.useCallback(
    (sObj) =>
      api.baseSearch(
        RequestSearch.fromJS({
          ...sObj,
          languageId: currentLanguage.languageId,
        })
      ),
    [api, currentLanguage.languageId]
  );

  return (
    <SearchTablePage
      columns={columns}
      getCriteriasFunction={() => api.getSearchCriterias(true)}
      searchFunction={search}
      exportFunction={(sObj) =>
        api.exportXlsFile(
          RequestSearch.fromJS({
            ...sObj,
            languageId: currentLanguage.languageId,
          })
        )
      }
      sortKeys={{ requestDate: "DESC", requestNumber: "DESC" }}
      breadCrumbs={[{ text: t(ETLCodes.Requests) }]}
    />
  );
};
